import { useState, useRef, useEffect } from 'react';
import { Shield, DollarSign, Star, Truck, MapPin, Phone, Mail, Clock, Cog, Award, Heart, Zap, Target, Users } from 'lucide-react';

const ICONS = { Shield, DollarSign, Star, Truck, MapPin, Phone, Mail, Clock, Cog, Award, Heart, Zap, Target, Users };

function InlineText({ value, onChange, tag: Tag = 'span', className = '', isEditing, placeholder = 'Click to edit...' }) {
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);

  const startEdit = (e) => {
    if (!isEditing) return;
    e.stopPropagation();
    setEditing(true);
    setTimeout(() => ref.current?.focus(), 0);
  };

  const finishEdit = () => {
    setEditing(false);
    if (ref.current && onChange) {
      const text = ref.current.innerText;
      if (text !== value) onChange(text);
    }
  };

  useEffect(() => {
    if (!editing && ref.current) ref.current.innerText = value || '';
  }, [value, editing]);

  return (
    <Tag
      ref={ref}
      className={`${className} ${isEditing ? 'cursor-text outline-none hover:ring-2 hover:ring-[#E31937]/30 rounded transition-all' : ''} ${editing ? 'ring-2 ring-[#E31937] bg-white/10 rounded px-1' : ''}`}
      contentEditable={editing}
      suppressContentEditableWarning
      onDoubleClick={startEdit}
      onBlur={finishEdit}
      onKeyDown={(e) => { if (e.key === 'Enter' && Tag !== 'p') { e.preventDefault(); finishEdit(); } }}
      data-placeholder={!value ? placeholder : undefined}
    >
      {value || placeholder}
    </Tag>
  );
}

function HeroSection({ props, isEditing, onUpdate }) {
  return (
    <section className="relative overflow-hidden" style={{ minHeight: props.compact ? '200px' : '400px' }}>
      {props.backgroundImage && (
        <img src={props.backgroundImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
      )}
      <div className="absolute inset-0 bg-[#0A0A0A]/70" />
      <div className="relative z-10 flex flex-col items-center justify-center text-center px-8" style={{ minHeight: props.compact ? '200px' : '400px' }}>
        <InlineText tag="h1" value={props.heading} isEditing={isEditing} onChange={(v) => onUpdate?.({ heading: v })}
          className="text-3xl md:text-5xl font-bold text-white uppercase tracking-tight max-w-4xl" placeholder="Hero Heading" />
        <InlineText tag="p" value={props.subheading} isEditing={isEditing} onChange={(v) => onUpdate?.({ subheading: v })}
          className="text-base md:text-lg text-zinc-300 mt-4 max-w-2xl" placeholder="Subheading text" />
        {(props.ctaText || isEditing) && (
          <div className="flex gap-3 mt-8">
            <div className="bg-[#E31937] text-white font-bold uppercase px-6 py-3 rounded-sm text-sm">
              <InlineText value={props.ctaText} isEditing={isEditing} onChange={(v) => onUpdate?.({ ctaText: v })} placeholder="Button Text" />
            </div>
            {(props.ctaSecondaryText || isEditing) && (
              <div className="border-2 border-white text-white font-bold uppercase px-6 py-3 rounded-sm text-sm">
                <InlineText value={props.ctaSecondaryText} isEditing={isEditing} onChange={(v) => onUpdate?.({ ctaSecondaryText: v })} placeholder="Secondary" />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function StatsSection({ props, isEditing, onUpdate }) {
  const updateItem = (idx, field, value) => {
    const items = [...(props.items || [])];
    items[idx] = { ...items[idx], [field]: value };
    onUpdate?.({ items });
  };
  return (
    <section className="bg-[#0A0A0A] py-8">
      <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
        {(props.items || []).map((s, i) => (
          <div key={i}>
            <InlineText tag="p" value={s.num} isEditing={isEditing} onChange={(v) => updateItem(i, 'num', v)}
              className="text-2xl md:text-3xl font-bold text-[#E31937]" placeholder="0" />
            <InlineText tag="p" value={s.label} isEditing={isEditing} onChange={(v) => updateItem(i, 'label', v)}
              className="text-xs text-zinc-300 uppercase tracking-wide mt-1" placeholder="Label" />
          </div>
        ))}
      </div>
    </section>
  );
}

function FeaturesSection({ props, isEditing, onUpdate }) {
  const updateItem = (idx, field, value) => {
    const items = [...(props.items || [])];
    items[idx] = { ...items[idx], [field]: value };
    onUpdate?.({ items });
  };
  return (
    <section className="py-12 md:py-16 bg-zinc-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <InlineText tag="h2" value={props.heading} isEditing={isEditing} onChange={(v) => onUpdate?.({ heading: v })}
          className="text-2xl md:text-3xl font-semibold text-[#0A0A0A] uppercase mb-8" placeholder="Section Heading" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {(props.items || []).map((f, i) => {
            const Icon = ICONS[f.icon] || Star;
            return (
              <div key={i} className="flex gap-4">
                <div className="w-12 h-12 bg-[#E31937]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Icon className="w-6 h-6 text-[#E31937]" />
                </div>
                <div>
                  <InlineText tag="h3" value={f.title} isEditing={isEditing} onChange={(v) => updateItem(i, 'title', v)}
                    className="font-semibold text-lg text-[#0A0A0A]" placeholder="Feature Title" />
                  <InlineText tag="p" value={f.description} isEditing={isEditing} onChange={(v) => updateItem(i, 'description', v)}
                    className="text-zinc-500 text-sm mt-1" placeholder="Feature description..." />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CTASection({ props, isEditing, onUpdate }) {
  return (
    <section className="relative py-16 overflow-hidden">
      {props.backgroundImage && <img src={props.backgroundImage} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-[#0A0A0A]/80" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
        <InlineText tag="h2" value={props.heading} isEditing={isEditing} onChange={(v) => onUpdate?.({ heading: v })}
          className="text-2xl md:text-3xl font-bold text-white uppercase" placeholder="CTA Heading" />
        <InlineText tag="p" value={props.subheading} isEditing={isEditing} onChange={(v) => onUpdate?.({ subheading: v })}
          className="text-zinc-300 mt-3 max-w-lg mx-auto" placeholder="CTA subheading" />
        {(props.ctaText || isEditing) && (
          <div className="inline-block mt-8 bg-[#E31937] text-white font-bold uppercase px-8 py-3 rounded-sm">
            <InlineText value={props.ctaText} isEditing={isEditing} onChange={(v) => onUpdate?.({ ctaText: v })} placeholder="Button Text" />
          </div>
        )}
      </div>
    </section>
  );
}

function TextBlockSection({ props, isEditing, onUpdate }) {
  return (
    <section className="py-12 md:py-16">
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${props.alignment === 'center' ? 'text-center' : ''}`}>
        <InlineText tag="h2" value={props.heading} isEditing={isEditing} onChange={(v) => onUpdate?.({ heading: v })}
          className="text-2xl md:text-3xl font-semibold text-[#0A0A0A] uppercase mb-6" placeholder="Heading" />
        <div className={`text-zinc-600 leading-relaxed space-y-4 ${props.alignment === 'center' ? 'max-w-2xl mx-auto' : ''}`}>
          <InlineText tag="p" value={props.content} isEditing={isEditing} onChange={(v) => onUpdate?.({ content: v })}
            className="whitespace-pre-line" placeholder="Add your content here..." />
        </div>
      </div>
    </section>
  );
}

function TextImageSection({ props, isEditing, onUpdate }) {
  const isTextLeft = props.layout !== 'text-right';
  return (
    <section className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 ${!isTextLeft ? 'direction-rtl' : ''}`}>
          <div className={!isTextLeft ? 'order-2 lg:order-1' : ''}>
            <InlineText tag="h2" value={props.heading} isEditing={isEditing} onChange={(v) => onUpdate?.({ heading: v })}
              className="text-2xl md:text-3xl font-semibold uppercase mb-6" placeholder="Heading" />
            <InlineText tag="p" value={props.content} isEditing={isEditing} onChange={(v) => onUpdate?.({ content: v })}
              className="text-zinc-600 leading-relaxed whitespace-pre-line" placeholder="Content..." />
          </div>
          <div className={`bg-zinc-100 rounded-sm flex items-center justify-center min-h-[250px] ${!isTextLeft ? 'order-1 lg:order-2' : ''}`}>
            {props.image ? (
              <img src={props.image} alt="" className="w-full h-full object-cover rounded-sm" />
            ) : (
              <div className="text-zinc-400 text-sm">{isEditing ? 'Set image in side panel' : ''}</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection({ props, isEditing, onUpdate }) {
  return (
    <section className="py-12 md:py-16 bg-zinc-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <InlineText tag="h2" value={props.heading} isEditing={isEditing} onChange={(v) => onUpdate?.({ heading: v })}
          className="text-2xl md:text-3xl font-semibold text-[#0A0A0A] uppercase mb-3" placeholder="Testimonials Heading" />
        <InlineText tag="p" value={props.subheading} isEditing={isEditing} onChange={(v) => onUpdate?.({ subheading: v })}
          className="text-zinc-500 mb-8" placeholder="Subheading" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-gray-100 shadow-sm p-6 rounded">
            <div className="flex gap-0.5 mb-3">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />)}</div>
            <p className="text-gray-600 text-sm leading-relaxed">"Testimonials are loaded from the CMS automatically."</p>
            <div className="border-t mt-4 pt-3"><p className="text-sm font-semibold">Auto-populated</p></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedInventorySection({ props, isEditing, onUpdate }) {
  return (
    <section className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <InlineText tag="h2" value={props.heading} isEditing={isEditing} onChange={(v) => onUpdate?.({ heading: v })}
              className="text-2xl md:text-3xl font-semibold text-[#0A0A0A] uppercase" placeholder="Section Heading" />
            <InlineText tag="p" value={props.subheading} isEditing={isEditing} onChange={(v) => onUpdate?.({ subheading: v })}
              className="text-zinc-500 mt-2" placeholder="Subheading" />
          </div>
          <InlineText value={props.linkText} isEditing={isEditing} onChange={(v) => onUpdate?.({ linkText: v })}
            className="text-[#E31937] font-medium text-sm" placeholder="Link Text" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="border border-gray-200 rounded bg-white overflow-hidden">
              <div className="aspect-video bg-zinc-100 flex items-center justify-center text-zinc-400 text-sm">Auto-populated from inventory</div>
              <div className="p-3">
                <p className="text-sm font-semibold text-gray-800">Featured Unit {i}</p>
                <p className="text-xs text-gray-400 mt-1">Loaded dynamically</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactCardsSection({ props, isEditing, onUpdate }) {
  const updateItem = (idx, field, value) => {
    const items = [...(props.items || [])];
    items[idx] = { ...items[idx], [field]: value };
    onUpdate?.({ items });
  };
  return (
    <section className="py-10 bg-[#0A0A0A] text-white">
      <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-4 gap-6">
        {(props.items || []).map((c, i) => {
          const Icon = ICONS[c.icon] || MapPin;
          return (
            <div key={i} className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[#E31937]/20 rounded-sm flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-[#E31937]" />
              </div>
              <div>
                <InlineText tag="p" value={c.title} isEditing={isEditing} onChange={(v) => updateItem(i, 'title', v)}
                  className="text-sm font-semibold" placeholder="Title" />
                <InlineText tag="p" value={c.text} isEditing={isEditing} onChange={(v) => updateItem(i, 'text', v)}
                  className="text-zinc-400 text-sm mt-0.5" placeholder="Detail" />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SpacerSection({ props }) {
  return <div style={{ height: props.height || 60 }} className="bg-transparent" />;
}

const RENDERERS = {
  hero: HeroSection,
  stats: StatsSection,
  features: FeaturesSection,
  cta: CTASection,
  text_block: TextBlockSection,
  text_image: TextImageSection,
  testimonials: TestimonialsSection,
  featured_inventory: FeaturedInventorySection,
  contact_cards: ContactCardsSection,
  spacer: SpacerSection,
};

export function SectionRenderer({ section, isEditing, isPreview, onUpdate }) {
  const Comp = RENDERERS[section.type];
  if (!Comp) return <div className="p-4 text-gray-400 text-sm">Unknown section type: {section.type}</div>;
  return <Comp props={section.props || {}} isEditing={isEditing && !isPreview} onUpdate={onUpdate} />;
}
